import { HTMLAttributes, forwardRef, ReactNode } from 'react'
import { clsx } from 'clsx'

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined' | 'glass' | 'flat'
  padding?: 'none' | 'sm' | 'md' | 'lg'
  hover?: boolean
  shadow?: boolean
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {}

interface CardContentProps extends HTMLAttributes<HTMLDivElement> {}

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {}

/**
 * Unified Card Component - Used throughout the entire application
 * Provides consistent styling and behavior across all pages
 */
const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ 
    className, 
    variant = 'default', 
    padding = 'none', // Default to none since CardHeader/CardContent handle padding
    hover = false, // Default to false for better control
    shadow = true,
    ...props 
  }, ref) => {
    const variants = {
      default: 'bg-white rounded-3xl ',
      elevated: 'bg-white rounded-3xl shadow-xl',
      outlined: 'bg-white rounded-3xl',
      glass: 'glass rounded-3xl',
      flat: 'bg-white rounded-3xl'
    }

    const paddings = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8'
    }

    const shadows = {
      true: 'shadow-lg',
      false: ''
    }

    return (
      <div
        ref={ref}
        className={clsx(
          variants[variant],
          paddings[padding],
          shadows[shadow.toString() as keyof typeof shadows],
          hover && 'hover:shadow-xl hover:-translate-y-1 transition-all duration-300',
          !hover && shadow && 'transition-shadow duration-300',
          className
        )}
        {...props}
      />
    )
  }
)

/**
 * Card Header - Contains title and description
 * Has consistent padding: p-6 pb-4
 */
const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx(
        'flex flex-col space-y-2 p-6 pb-4',
        className
      )}
      {...props}
    />
  )
)

/**
 * Card Title - Consistent typography for card titles
 * Default: h3, can be changed with 'as' prop
 */
const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = 'h3', ...props }, ref) => {
    return (
      <Component
        ref={ref}
        style={{ fontFeatureSettings: '"rlig" 1, "calt" 1' }}
        className={clsx(
          'text-xl font-semibold leading-tight tracking-tight text-gray-900',
          className
        )}
        {...props}
      />
    )
  }
)

/**
 * Card Description - Subtle text below title
 */
const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      style={{ fontFeatureSettings: '"rlig" 1, "calt" 1' }}
      className={clsx(
          'text-sm text-gray-700 leading-relaxed',
        className
      )}
      {...props}
    />
  )
)

/**
 * Card Content - Main content area
 * Has consistent padding: p-6 pt-2 (if header exists) or p-6 (if standalone)
 */
const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    <div 
      ref={ref} 
      className={clsx(
        'p-6 pt-2',
        className
      )} 
      {...props} 
    />
  )
)

/**
 * Card Footer - Action area at bottom
 * Has border-top separator and consistent padding
 */
const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={clsx(
        'flex items-center justify-between p-6 pt-4',
        'border-t border-secondary-100/60',
        className
      )}
      {...props}
    />
  )
)

Card.displayName = 'Card'
CardHeader.displayName = 'CardHeader'
CardTitle.displayName = 'CardTitle'
CardDescription.displayName = 'CardDescription'
CardContent.displayName = 'CardContent'
CardFooter.displayName = 'CardFooter'

export { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent, 
  CardFooter 
}

export type {
  CardProps,
  CardHeaderProps,
  CardTitleProps,
  CardDescriptionProps,
  CardContentProps,
  CardFooterProps
}